import { Plus, Loader2 } from 'lucide-react'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useROIStore } from './store'
import { useHomeStore } from '@/features/home/store'
import { useAuthStore } from '@store/authStore'
import { roiApi, dealsApi, draftsApi } from '@api/client'
import { getProduct } from './data'
import { ROIHeader } from './components/ROIHeader'
import { RegionCard } from './components/RegionCard'
import { CustomerRegionSummary } from './components/CustomerRegionSummary'
import { FlowVisualization } from './components/FlowVisualization'
import { ROIAnalysis } from './components/ROIAnalysis'
import { BenchmarkReference } from './components/BenchmarkReference'
import { AddProductModal } from './components/AddProductModal'
import { useAutoSave } from '@hooks/useAutoSave'
import { AutoSaveIndicator } from '@hooks/AutoSaveIndicator'
import { LastEditedIndicator } from '@components/ui/LastEditedIndicator'
import { ChangeHistory } from '@components/ui/ChangeHistory'
import type { Region } from './types'

export function ROICalculator() {
  const { regions, activeRegionId, selectedFlowRegionId, setSelectedFlowRegion, addRegion, isCustomerView, loadROIState, setCustomerView, aggregatedResults } = useROIStore()
  const { selectDeal } = useHomeStore()
  const { user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [addProductModal, setAddProductModal] = useState<{ isOpen: boolean; regionId: number | null }>({
    isOpen: false,
    regionId: null,
  })
  const [lastEditMeta, setLastEditMeta] = useState<{ updatedAt?: string | null; updatedByName?: string | null }>({})

  const sessionId = searchParams.get('session')
  const draftId = searchParams.get('draft')
  const draftIdRef = useRef<string | null>(draftId && draftId !== 'new' ? draftId : null)
  const regionsParam = searchParams.get('regions')
  const viewMode = searchParams.get('view')

  // Track the existing ROI record ID so auto-save can update instead of creating duplicates
  const roiRecordIdRef = useRef<number | null>(null)

  // Auto-save: debounce saves to DB as user edits
  const autoSaveData = useMemo(() => ({ regions }), [regions])

  const handleAutoSave = useCallback(async () => {
    if (sessionId) {
      // Deal-based save (existing behaviour)
      const roiState = { regions }
      const dealName = useHomeStore.getState().selectedDeal?.companyName
        || useHomeStore.getState().selectedDeal?.name || ''

      if (roiRecordIdRef.current) {
        const response = await roiApi.update(roiRecordIdRef.current, {
          inputs_json: JSON.stringify(roiState),
          outputs_json: aggregatedResults ? JSON.stringify(aggregatedResults) : undefined,
          customer_name: dealName,
          _user_email: user?.email,
          _user_name: user?.name,
        } as any)
        if (!response.success) throw new Error(response.error || 'Failed to save ROI')
      } else {
        const existing = await roiApi.getByDealId(Number(sessionId))
        if (existing.success && existing.data) {
          roiRecordIdRef.current = existing.data.id
          const response = await roiApi.update(existing.data.id, {
            inputs_json: JSON.stringify(roiState),
            outputs_json: aggregatedResults ? JSON.stringify(aggregatedResults) : undefined,
            customer_name: dealName,
            _user_email: user?.email,
            _user_name: user?.name,
          } as any)
          if (!response.success) throw new Error(response.error || 'Failed to save ROI')
        } else {
          const createResponse = await roiApi.create({
            deal_id: Number(sessionId),
            customer_name: dealName,
            inputs_json: JSON.stringify(roiState),
            outputs_json: aggregatedResults ? JSON.stringify(aggregatedResults) : undefined,
            status: 'draft',
            _user_email: user?.email,
            _user_name: user?.name,
          } as any)
          if (createResponse.success && createResponse.data) {
            roiRecordIdRef.current = createResponse.data.id
          } else {
            throw new Error(createResponse.error || 'Failed to create ROI record')
          }
        }
      }
    } else if (draftIdRef.current || draftId === 'new') {
      // Draft save
      const payload = {
        data_json: JSON.stringify({ regions }),
        customer_name: null,
        title: 'Untitled ROI',
        feature_type: 'roi' as const,
        user_email: user?.email,
        user_name: user?.name,
      }
      if (!draftIdRef.current) {
        const res = await draftsApi.create(payload)
        if (res.success && res.data) {
          draftIdRef.current = String(res.data.id)
          setSearchParams({ draft: String(res.data.id) }, { replace: true })
        } else {
          throw new Error(res.error || 'Failed to create draft')
        }
      } else {
        const res = await draftsApi.update(Number(draftIdRef.current), payload)
        if (!res.success) {
          throw new Error(res.error || 'Failed to save draft')
        }
      }
    }
  }, [sessionId, draftId, regions, aggregatedResults, user, setSearchParams])

  const { status: autoSaveStatus, retryCount, lastError, retry: retrySave } = useAutoSave({
    data: autoSaveData,
    onSave: handleAutoSave,
    enabled: (!!sessionId || !!draftId) && !isLoadingSession,
    debounceMs: 3000,
  })

  // Load from URL-encoded regions param (customer links — no DB needed)
  useEffect(() => {
    if (!regionsParam) return

    try {
      const regionsData = JSON.parse(regionsParam) as Array<{
        n?: string; c: string; i: string; p: string; t: string
        r: number; v: number; val: number; s: string
      }>

      const loadedRegions: Region[] = regionsData.map((rd, idx) => {
        const stackProfiles = rd.s ? rd.s.split('|').filter(Boolean) : []
        const stack = stackProfiles
          .map(profileId => {
            const product = getProduct(profileId)
            return product ? {
              profile: product.profile,
              description: product.description,
              passRate: product.passRate,
              incrementalLift: product.incrementalLift,
            } : null
          })
          .filter((item): item is NonNullable<typeof item> => item !== null)

        return {
          id: idx + 1,
          name: rd.n || '',
          country: rd.c || '',
          industry: rd.i || '',
          provider: rd.p || '',
          product: rd.t || '',
          passRate: rd.r || 0,
          volume: rd.v || 0,
          valuePerCustomer: rd.val || 0,
          costPerCheck: 0,
          stack,
        }
      })

      if (loadedRegions.length > 0) {
        loadROIState({ regions: loadedRegions })
      }

      if (viewMode === 'customer') {
        setCustomerView(true)
      }
    } catch (e) {
      console.error('Failed to parse ROI regions from URL:', e)
    }
  }, [regionsParam])

  // Load session data when ?session= param is present (internal links)
  const loadSession = useCallback(async () => {
    if (!sessionId) return

    setIsLoadingSession(true)
    setSessionError(null)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    try {
      // Fetch the deal info
      const dealResponse = await dealsApi.get(Number(sessionId))
      if (!dealResponse.success || !dealResponse.data) {
        setSessionError('Deal not found')
        setIsLoadingSession(false)
        clearTimeout(timeoutId)
        return
      }

      const deal = dealResponse.data
      selectDeal({
        id: String(deal.id),
        hubspotDealId: deal.hubspot_deal_id,
        name: deal.hubspot_deal_name || deal.customer_name,
        amount: null,
        stage: deal.status,
        closeDate: null,
        ownerName: deal.sales_owner || '',
        companyName: deal.customer_name,
        createdAt: deal.created_at,
      })

      // Fetch the ROI data
      const roiResponse = await roiApi.getByDealId(Number(sessionId))
      if (roiResponse.success && roiResponse.data) {
        const roiData = roiResponse.data
        roiRecordIdRef.current = roiData.id
        setLastEditMeta({
          updatedAt: roiData.updated_at,
          updatedByName: roiData.updated_by_name,
        })
        if (roiData.inputs_json) {
          try {
            const inputState = typeof roiData.inputs_json === 'string'
              ? JSON.parse(roiData.inputs_json)
              : roiData.inputs_json

            if (inputState.regions) {
              loadROIState({ regions: inputState.regions })
            }
          } catch (e) {
            console.error('Failed to parse ROI inputs_json:', e)
          }
        }
      }

      if (viewMode === 'customer') {
        setCustomerView(true)
      }
    } catch (error) {
      console.error('Failed to load ROI session:', error)
      if (error instanceof DOMException && error.name === 'AbortError') {
        setSessionError('Loading timed out — the server may be slow. Please try again.')
      } else {
        setSessionError('Failed to load ROI session data')
      }
    } finally {
      clearTimeout(timeoutId)
      setIsLoadingSession(false)
    }
  }, [sessionId, selectDeal, loadROIState, viewMode, setCustomerView])

  useEffect(() => {
    if (sessionId) loadSession()
  }, [sessionId])

  // Load draft data when ?draft= param is present (not 'new')
  useEffect(() => {
    if (!draftId || draftId === 'new' || sessionId) return

    const loadDraft = async () => {
      setIsLoadingSession(true)
      setSessionError(null)
      try {
        const res = await draftsApi.get(Number(draftId))
        if (res.success && res.data) {
          const parsed = JSON.parse(res.data.data_json)
          if (parsed.regions) {
            loadROIState({ regions: parsed.regions })
          }
        } else {
          setSessionError('Draft not found')
        }
      } catch (error) {
        console.error('Failed to load ROI draft:', error)
        setSessionError('Failed to load draft data')
      } finally {
        setIsLoadingSession(false)
      }
    }

    loadDraft()
  }, [draftId, sessionId])

  const openAddProductModal = (regionId: number) => {
    setAddProductModal({ isOpen: true, regionId })
  }

  const closeAddProductModal = () => {
    setAddProductModal({ isOpen: false, regionId: null })
  }

  if (isLoadingSession) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-[var(--text-muted)]">Loading ROI session...</p>
        </div>
      </div>
    )
  }

  if (sessionError) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-sm text-danger mb-2">{sessionError}</p>
          <button
            onClick={loadSession}
            className="mt-2 px-4 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Customer view: full-width single column with tab navigation
  if (isCustomerView) {
    const getRegionLabel = (region: Region) => region.name || region.country || `Region ${region.id}`

    return (
      <div className="w-full space-y-4">
        {/* Header */}
        <ROIHeader />

        {/* Region tabs (only show if multiple regions) */}
        {regions.length > 1 && (
          <div className="flex gap-2">
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setSelectedFlowRegion(region.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedFlowRegionId === region.id
                    ? 'bg-primary text-white'
                    : 'bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-primary/50'
                }`}
              >
                {getRegionLabel(region)}
              </button>
            ))}
          </div>
        )}

        {/* Selected region summary */}
        {regions
          .filter((r) => r.id === selectedFlowRegionId || (!selectedFlowRegionId && r.id === regions[0]?.id))
          .map((region) => (
            <CustomerRegionSummary key={region.id} region={region} />
          ))}

        {/* Flow + Analysis */}
        <div className="space-y-4">
          <FlowVisualization />
          <ROIAnalysis />
          <BenchmarkReference />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <ROIHeader />

      {/* Auto-save indicator + last edited */}
      {(sessionId || draftId) && (
        <div className="flex items-center justify-between -mt-2">
          <LastEditedIndicator updatedAt={lastEditMeta.updatedAt} updatedByName={lastEditMeta.updatedByName} />
          <AutoSaveIndicator status={autoSaveStatus} retryCount={retryCount} lastError={lastError} onRetry={retrySave} />
        </div>
      )}

      {/* Aggregate Summary - show when multiple regions */}
      {regions.length > 1 && aggregatedResults && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">All Regions Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-[var(--text-muted)]">Total Volume</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">{aggregatedResults.totalVolume.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[var(--text-muted)]">Additional Conversions</p>
              <p className="text-lg font-semibold text-success">+{Math.round(aggregatedResults.totalAdditionalConversions).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[var(--text-muted)]">Monthly Value</p>
              <p className="text-lg font-semibold text-primary">${Math.round(aggregatedResults.totalMonthlyValue).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[var(--text-muted)]">Annual Value</p>
              <p className="text-lg font-semibold text-primary">${Math.round(aggregatedResults.totalAnnualValue).toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-2 text-xs text-[var(--text-muted)]">
            Avg pass rate improvement: +{aggregatedResults.avgImprovement.toFixed(1)}pp across {regions.length} regions
          </div>
        </div>
      )}

      {/* Main Layout - Two Columns (Customer Regions narrower, Flow wider) */}
      <div className="grid grid-cols-1 lg:grid-cols-[0.7fr_1.8fr] gap-4">
        {/* Left Column - Input Section */}
        <div className="space-y-4">
          {/* Regions Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-[var(--text-primary)]">Customer Regions</h2>
            <button
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
              onClick={() => addRegion()}
            >
              <Plus size={14} />
              Add Region
            </button>
          </div>

          {/* Region Cards */}
          <div className="space-y-3">
            {regions.map((region) => (
              <RegionCard
                key={region.id}
                region={region}
                isActive={activeRegionId === region.id}
                canRemove={regions.length > 1}
                onAddProduct={() => openAddProductModal(region.id)}
              />
            ))}
          </div>
        </div>

        {/* Right Column - Results Section */}
        <div className="space-y-4">
          {/* Flow Visualization */}
          <FlowVisualization />

          {/* ROI Analysis */}
          <ROIAnalysis />

          {/* Benchmark Reference */}
          <BenchmarkReference />

          {/* Change History */}
          {sessionId && <ChangeHistory dealId={Number(sessionId)} table="roi_calculations" />}
        </div>
      </div>

      {/* Add Product Modal */}
      {addProductModal.regionId !== null && (
        <AddProductModal
          isOpen={addProductModal.isOpen}
          onClose={closeAddProductModal}
          regionId={addProductModal.regionId}
        />
      )}
    </div>
  )
}
